import { Form } from "@unform/web";
import {
  BadgeDollarSign,
  Building,
  DollarSign,
  HandCoins,
  Mail,
  MessageSquare,
  PlayCircle,
  Settings,
  XCircle,
} from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Input from "~/components/RegisterForm/Input";

import RegisterFormMenu from "~/components/RegisterForm/Menu";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/RegisterForm/FormHeader";
import Preview from "~/components/Preview";
import { Zoom, toast } from "react-toastify";
import api from "~/services/api";
import { formatter, getRegistries } from "~/functions";
import SelectPopover from "~/components/RegisterForm/SelectPopover";
import FormLoading from "~/components/RegisterForm/FormLoading";
import { useSelector } from "react-redux";
import DatePicker from "~/components/RegisterForm/DatePicker";
import { format, isValid, parse, parseISO } from "date-fns";
import { yesOrNoOptions } from "~/functions/selectPopoverOptions";
import { openPaymentModal } from "~/functions/emergepayfn";
import { AlertContext } from "~/App";
import PricesSimulation from "~/components/PricesSimulation";
import { FullGridContext } from "../../..";
import Textarea from "~/components/RegisterForm/Textarea";
import FindGeneric from "~/components/Finds/FindGeneric";
import Comments from "./Comments";

export const InputContext = createContext({});

export default function PagePreview({
  access,
  id,
  defaultFormType = "preview",
}) {
  const { alertBox } = useContext(AlertContext);
  const {
    handleOpened,
    setOpened,
    successfullyUpdated,
    setSuccessfullyUpdated,
    gridData,
  } = useContext(FullGridContext);
  const [pageData, setPageData] = useState({
    loaded: false,
    filial: { name: "" },
    issuer: { student: { name: "", last_name: "", discounts: [], enrollments: [] } },
    receivables: [],
  });

  const [registry, setRegistry] = useState({
    created_by: null,
    created_at: null,
    updated_by: null,
    updated_at: null,
    canceled_by: null,
    canceled_at: null,
  });
  const [formType, setFormType] = useState(defaultFormType);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("general");
  const [isAutoPay, setIsAutoPay] = useState(false);
  const [paid, setPaid] = useState(false);
  const [savedDefaults, setSavedDefaults] = useState(null);
  const [isGlobal, setIsGlobal] = useState(false);
  const [globalSession, setGlobalSession] = useState(null);
  const [globalPaymentMethod, setGlobalPaymentMethod] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);

  const auth = useSelector((state) => state.auth);
  const isCanceled = pageData?.recurrence_status === "Canceled";
  const isActive = pageData?.recurrence_status === "Active";
  const student = pageData?.issuer?.student ?? pageData?.student ?? {};
  const simulationStudent = useMemo(
    () => ({
      ...student,
      searchFields: {
        filial_id: pageData?.filial_id,
        processtype_id: student?.processtype_id ?? null,
        processsubstatus_id: student?.processsubstatus_id ?? null,
      },
    }),
    [pageData]
  );

  const generalForm = useRef();

  function parseDateValue(value) {
    if (!value) return null;
    if (value instanceof Date) return isValid(value) ? value : null;
    const isoDate = parseISO(value);
    if (isValid(isoDate)) return isoDate;
    const mdYDate = parse(value, "MM-dd-yyyy", new Date());
    if (isValid(mdYDate)) return mdYDate;
    return null;
  }

  function handleCloseForm() {
    if (!successfullyUpdated) {
      toast("Changes discarted!", { autoClose: 1000 });
    }
    handleOpened(null);
  }

  async function handleGeneralFormSubmit(data) {
    if (successfullyUpdated) {
      toast("No need to be saved!", {
        autoClose: 1000,
        type: "info",
        transition: Zoom,
      });
      return;
    }
    try {
      const { entry_date, in_class_date, first_due_date } = data;

      if (
        data.paymentMethod?.id ||
        data.paymentCriteria?.id ||
        data.chartOfAccount?.id ||
        data.is_autopay !== undefined
      ) {
        const dataToSave = {
          paymentMethod: data.paymentMethod || {},
          paymentCriteria: data.paymentCriteria || {},
          chartOfAccount: data.chartOfAccount || {},
          is_autopay: data.is_autopay,
        };
        localStorage.setItem(
          "lastRecurrenceDefaults",
          JSON.stringify(dataToSave)
        );
      }

      const normalizeDiscountDate = (value) => {
        if (!value) return null;
        const parsed = value instanceof Date ? value : parseISO(value);
        return isValid(parsed) ? format(parsed, "yyyy-MM-dd") : null;
      };
      const pricesPayload = data.prices
        ? {
            ...data.prices,
            discounts: (data.prices.discounts || []).map((discount) => ({
              ...discount,
              start_date: normalizeDiscountDate(discount.start_date),
              end_date: normalizeDiscountDate(discount.end_date),
            })),
          }
        : data.prices;
      if (pricesPayload) {
        delete pricesPayload.financialDiscountChoose;
        delete pricesPayload.financialDiscountFromDate;
        delete pricesPayload.financialDiscountUntilDate;
      }
      const recurrenceBody = {
        ...data,
        prices: pricesPayload,
        entry_date: entry_date ? format(entry_date, "yyyy-MM-dd") : null,
        in_class_date: in_class_date
          ? format(in_class_date, "yyyy-MM-dd")
          : null,
        first_due_date: first_due_date
          ? format(first_due_date, "yyyy-MM-dd")
          : null,
      };
      console.log("[Recurrence] POST /recurrence/ body completo:", recurrenceBody);
      console.log("[Recurrence] body.prices:", recurrenceBody.prices);
      console.log(
        "[Recurrence] body.prices.discounts:",
        recurrenceBody.prices?.discounts,
        "| financialDiscountChoose:",
        recurrenceBody.prices?.financialDiscountChoose,
        "| financialDiscountFromDate:",
        recurrenceBody.prices?.financialDiscountFromDate,
        "| financialDiscountUntilDate:",
        recurrenceBody.prices?.financialDiscountUntilDate
      );
      const { data: postedData } = await api.post(`/recurrence/`, recurrenceBody);

      setPageData({
        ...pageData,
        ...data,
        loaded: false,
      });
      toast("Saved!", { autoClose: 1000 });
      if (postedData?.warning) {
        const invoices = Array.isArray(postedData?.skipped_overdue_invoices)
          ? postedData.skipped_overdue_invoices.join(", ")
          : null;
        const warningMessage = invoices
          ? `${postedData.warning} Invoices: ${invoices}.`
          : postedData.warning;
        const formattedWarningMessage = warningMessage.replace(
          /\.\s+/g,
          ".<br/>",
        );
        alertBox({
          title: "Attention!",
          descriptionHTML: `<p>${formattedWarningMessage}</p>`,
          buttons: [
            {
              title: "Ok",
              class: "cancel",
            },
          ],
        });
      }
      setSuccessfullyUpdated(true);

      setTimeout(async () => {
        const { data: recurrenceData } = await api.get(`/recurrence/${postedData.id}`);
        api
          .get(`/recurrence/receivables/${postedData.id}`)
          .then(async ({ data }) => {
            const receivables = data.rows;
            setPageData({
              ...recurrenceData,
              receivables,
              loaded: true,
            });
            setActiveMenu("receivables");
            if (recurrenceData.is_autopay) {
              alertBox({
                title: "Attention!",
                descriptionHTML: `<p>Do you want to configure the payment method for this recurrence?</p>`,
                buttons: [
                  {
                    title: "No",
                    class: "cancel",
                  },
                  {
                    title: "Yes",
                    onPress: async () => {
                      const receivable = receivables
                        .filter((receivable) => receivable.status === "Pending")
                        .sort((a, b) => a.due_date - b.due_date)[0];
                      await openPaymentModal({
                        receivable,
                        recurrence_id: postedData.id,
                      });
                    },
                  },
                ],
              });
            }
          });
      }, 2000);
    } catch (err) {
      const responseData = err?.response?.data;
      const invoices = Array.isArray(responseData?.invoices)
        ? responseData.invoices.join(", ")
        : null;
      const errorMessage = responseData?.error
        ? invoices
          ? `${responseData.error} Invoices: ${invoices}.`
          : responseData.error
        : "Unexpected error.";
      const formattedErrorMessage = errorMessage.replace(/\.\s+/g, ".<br/>");
      alertBox({
        title: "Action required",
        descriptionHTML: `<p>The student has outstanding receivables including fees.</p><p>Reprocessing of recurring payments can only be perfomed if the fee is removed from the invoices.</p><p>${formattedErrorMessage}</p>`,
        buttons: [
          {
            title: "Ok",
            class: "cancel",
          },
        ],
      });
      console.log(err);
    }
  }
  async function getPageData() {
    try {
      const currentRow = gridData?.find((row) => row.id === id);
      const isNewRecurrence = currentRow?.isNewRecurrence === true;

      if (currentRow?.isGlobalSession) {
        const { data } = await api.get(`/contractsessions/${id}`);
        const student = data.contract?.student ?? {};
        const pmData = await api.get(`/paymentmethods`);
        setPaymentMethods(
          (pmData.data.rows || [])
            .filter((p) => p.is_available_at_enrollment)
            .map((p) => ({ value: p.id, label: p.description.slice(0, 20) }))
        );
        setIsGlobal(true);
        setGlobalSession(data.global_session ?? null);
        setPageData({
          filial: data.contract?.filial ?? {},
          filial_id: student.filial_id ?? null,
          issuer: student.issuer ?? null,
          issuer_id: student.issuer?.id ?? null,
          receivables: [],
          loaded: true,
          student,
        });
        setActiveMenu("invoices");
        return;
      }

      if (isNewRecurrence) {
        const { data } = await api.get(`/recurrence-newstudent/${id}`);
        setPageData({
          filial: data.filial,
          filial_id: data.filial_id,
          issuer: data.issuer ?? null,
          issuer_id: data.issuer?.id ?? null,
          entry_date: new Date(),
          in_class_date: data.start_date ?? null,
          receivables: [],
          loaded: true,
          student: data,
        });
        return;
      }

      const { data } = await api.get(`/recurrence/${id}`);

      const { data: dataRec } = await api.get(`/recurrence/receivables/${id}`);
      const receivables = dataRec.rows ?? [];

      setPageData({
        ...data,
        receivables,
        loaded: true,
      });
      setIsAutoPay(data.is_autopay);

      const {
        created_by,
        created_at,
        updated_by,
        updated_at,
        canceled_by,
        canceled_at,
      } = data;
      const registries = await getRegistries({
        created_by,
        created_at,
        updated_by,
        updated_at,
        canceled_by,
        canceled_at,
      });
      setRegistry(registries);
    } catch (err) {
      console.log(err);
      toast(err?.response?.data?.error, { type: "error", autoClose: 3000 });
    }
  }

  async function handleGlobalSendInvoice(receivable) {
    if (!globalPaymentMethod) {
      toast.error("Please select a Payment Method.");
      return;
    }
    try {
      await api.post(`/prospect_payments/payment_link`, {
        issuer_id: pageData.issuer_id,
        registrationFee_id:
          receivable.type_detail === "Registration fee" ? receivable.id : null,
        tuitionFee_id:
          receivable.type_detail === "Tuition fee" ? receivable.id : null,
        paymentmethod_id: globalPaymentMethod,
        enrollment_id: student?.enrollments?.[0]?.id,
        student_id: student?.id,
      });
      toast.success("Invoice link sent to student!");
    } catch (err) {
      toast.error("Failed to send invoice link.");
    }
  }

  async function handleGlobalProcessPayment(receivable) {
    try {
      await openPaymentModal({
        receivable: {
          id: receivable.id,
          total: parseFloat(receivable.amount || 0),
          memo: receivable.type_detail,
        },
      });
      setGlobalSession((prev) =>
        prev
          ? {
              ...prev,
              receivables: prev.receivables.map((r) =>
                r.id === receivable.id ? { ...r, status: "Paid" } : r
              ),
            }
          : prev
      );
    } catch (err) {
      toast.error("Failed to open payment modal.");
    }
  }

  useEffect(() => {
    const savedData = localStorage.getItem("lastRecurrenceDefaults");
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setSavedDefaults(parsed);
      } catch (error) {
        console.error("Error parsing saved defaults:", error);
      }
    }
    getPageData();
  }, []);

  useEffect(() => {
    if (paid) {
      setPageData({
        ...pageData,
        loaded: false,
      });
      try {
        setPaid(false);
        getPageData();
      } catch (err) {
        console.log(err);
      }
    }
  }, [paid]);

  return (
    <Preview formType={formType} fullscreen={fullscreen}>
      {pageData ? (
        <div className="flex h-full flex-row items-start justify-between gap-4">
          <div className="flex flex-col items-center justify-between text-xs w-32 gap-4">
            <RegisterFormMenu
              setActiveMenu={setActiveMenu}
              activeMenu={activeMenu}
              name="general"
            >
              <Building size={16} /> General
            </RegisterFormMenu>

            {!isGlobal && (
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="receivables"
              >
                <HandCoins size={16} /> Receivables
              </RegisterFormMenu>
            )}
            {isGlobal && (
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="invoices"
              >
                <HandCoins size={16} /> Invoices
              </RegisterFormMenu>
            )}

            <RegisterFormMenu
              setActiveMenu={setActiveMenu}
              activeMenu={activeMenu}
              name="comments"
            >
              <MessageSquare size={16} /> Enrollment Comments
            </RegisterFormMenu>

            {isCanceled && (
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="canceled"
              >
                <XCircle size={16} /> Canceled
              </RegisterFormMenu>
            )}
          </div>
          <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
            <div className="flex flex-col items-start justify-start text-sm overflow-y-scroll">
              <Form
                ref={generalForm}
                onSubmit={handleGeneralFormSubmit}
                className="w-full pb-32"
              >
                <InputContext.Provider
                  value={{
                    id,
                    generalForm,
                    setSuccessfullyUpdated,
                    fullscreen,
                    setFullscreen,
                    successfullyUpdated,
                    handleCloseForm,
                    isCanceled,
                  }}
                >
                  {id === "new" || pageData.loaded ? (
                    <>
                      <FormHeader
                        access={access}
                        title={student?.name}
                        registry={registry}
                        InputContext={InputContext}
                        disableSave={isCanceled}
                      />

                      <InputLineGroup
                        title="GENERAL"
                        activeMenu={activeMenu === "general"}
                      >
                        <FindGeneric
                          route="filials"
                          title="Filial"
                          scope="filial"
                          required
                          readOnly={isCanceled}
                          InputContext={InputContext}
                          defaultValue={
                            id === "new" && auth.filial.id !== 1
                              ? {
                                  id: auth.filial.id,
                                  name: auth.filial.name,
                                }
                              : {
                                  id: pageData.filial?.id,
                                  name: pageData.filial?.name,
                                }
                          }
                          fields={[
                            {
                              title: "Name",
                              name: "name",
                            },
                          ]}
                        />
                        <InputLine title="Student">
                          <Input
                            type="hidden"
                            name="student_id"
                            readOnly={true}
                            grow
                            defaultValue={student?.id}
                            InputContext={InputContext}
                          />
                          <Input
                            type="text"
                            name="student_name"
                            readOnly={true}
                            grow
                            title="Student Name"
                            defaultValue={student?.name + " " + student?.last_name}
                            InputContext={InputContext}
                          />
                        </InputLine>

                        <Input
                          type="hidden"
                          name="issuer_id"
                          readOnly={true}
                          grow
                          defaultValue={pageData?.issuer?.id}
                          InputContext={InputContext}
                        />

                        <PricesSimulation
                          student={simulationStudent}
                          InputContext={InputContext}
                          FullGridContext={FullGridContext}
                          generalForm={generalForm}
                          showAdmissionDiscounts={false}
                          isAdmissionDiscountChangable={false}
                          showFinancialDiscounts={true}
                          isFinancialDiscountChangable={!isCanceled}
                          recurrence={true}
                        />

                        <InputLine title="Recurrence Information">
                          <DatePicker
                            name="entry_date"
                            grow
                            required
                            disabled
                            title="Entry Date "
                            defaultValue={
                              pageData?.entry_date
                                ? parseDateValue(pageData.entry_date)
                                : new Date()
                            }
                            placeholderText="MM/DD/YYYY"
                            InputContext={InputContext}
                          />

                          <DatePicker
                            name="in_class_date"
                            grow
                            required
                            disabled={isCanceled}
                            title="In Class Date "
                            defaultValue={
                              pageData?.in_class_date
                                ? parseDateValue(pageData.in_class_date)
                                : student?.start_date
                                ? parseDateValue(student.start_date)
                                : null
                            }
                            placeholderText="MM/DD/YYYY"
                            InputContext={InputContext}
                          />
                          <DatePicker
                            name="first_due_date"
                            grow
                            required
                            disabled={isCanceled || pageData.receivables?.find(
                              (receivable) => receivable.status === "Paid"
                            )}
                            title="First Due Date"
                            defaultValue={
                              pageData?.first_due_date
                                ? parseDateValue(pageData.first_due_date)
                                : null
                            }
                            placeholderText="MM/DD/YYYY"
                            InputContext={InputContext}
                          />
                        </InputLine>
                        <FindGeneric
                          route="paymentmethods"
                          title="Payment Methods"
                          scope="paymentMethod"
                          required
                          readOnly={isCanceled}
                          InputContext={InputContext}
                          defaultValue={
                            pageData?.paymentMethod?.id
                              ? {
                                  id: pageData.paymentMethod.id,
                                  description: pageData.paymentMethod.description,
                                  platform: pageData.paymentMethod.platform,
                                }
                              : savedDefaults?.paymentMethod?.id
                              ? savedDefaults.paymentMethod
                              : {}
                          }
                          fields={[
                            {
                              title: "Description",
                              name: "description",
                            },
                            {
                              title: "Platform",
                              name: "platform",
                            },
                          ]}
                        />
                        <FindGeneric
                          route="paymentcriterias"
                          title="Payment Criterias"
                          scope="paymentCriteria"
                          required
                          readOnly={isCanceled || isActive}
                          InputContext={InputContext}
                          defaultValue={
                            pageData?.paymentCriteria?.id
                              ? {
                                  id: pageData.paymentCriteria.id,
                                  description: pageData.paymentCriteria.description,
                                }
                              : savedDefaults?.paymentCriteria?.id
                              ? savedDefaults.paymentCriteria
                              : {}
                          }
                          fields={[
                            {
                              title: "Description",
                              name: "description",
                            },
                          ]}
                        />
                        <FindGeneric
                          route="chartofaccounts"
                          title="Chart of Account"
                          scope="chartOfAccount"
                          required
                          type="receipts"
                          readOnly={isCanceled}
                          InputContext={InputContext}
                          defaultValue={
                            pageData?.chartOfAccount?.id
                              ? {
                                  id: pageData.chartOfAccount.id,
                                  code: pageData.chartOfAccount.code,
                                  name: pageData.chartOfAccount.name,
                                  father: pageData.chartOfAccount?.Father?.name,
                                  granFather: pageData.chartOfAccount?.Father?.Father?.name,
                                }
                              : savedDefaults?.chartOfAccount?.id
                              ? savedDefaults.chartOfAccount
                              : {}
                          }
                          fields={[
                            {
                              title: "Code",
                              name: "code",
                            },
                            {
                              title: "Name",
                              name: "name",
                            },
                            {
                              title: "Father",
                              model: "Father",
                              name: "name",
                            },
                            {
                              title: "Father",
                              model: "Father",
                              model2: "Father",
                              name: "name",
                            },
                          ]}
                        />
                        <FindGeneric
                          route="costcenters"
                          title="Cost Centers"
                          scope="costCenter"
                          readOnly={isCanceled}
                          InputContext={InputContext}
                          defaultValue={{
                            id: pageData?.costCenter?.id,
                            code: pageData?.costCenter?.code,
                            name: pageData?.costCenter?.name,
                            father: pageData?.costCenter?.Father?.name,
                            granFather: pageData?.costCenter?.Father?.Father?.name,
                          }}
                          fields={[
                            {
                              title: "Code",
                              name: "code",
                            },
                            {
                              title: "Name",
                              name: "name",
                            },
                            {
                              title: "Father",
                              model: "Father",
                              name: "name",
                            },
                            {
                              title: "Father",
                              model: "Father",
                              model2: "Father",
                              name: "name",
                            },
                          ]}
                        />
                        <InputLine>
                          <Textarea
                            name="memo"
                            title="Memo"
                            grow
                            rows={5}
                            readOnly={isCanceled}
                            onChange={(el) => {
                              if (!isCanceled) setSuccessfullyUpdated(false);
                            }}
                            InputContext={InputContext}
                            defaultValue={pageData?.memo ?? ""}
                          />
                        </InputLine>
                        {!isGlobal && <InputLine title="Autopay">
                          <SelectPopover
                            name="is_autopay"
                            title="Is Autopay?"
                            required
                            grow
                            readOnly={isCanceled}
                            onChange={(el) => {
                              if (!isCanceled) {
                                setIsAutoPay(el.value);
                                setSuccessfullyUpdated(false);
                              }
                            }}
                            defaultValue={
                              pageData?.is_autopay !== undefined
                                ? yesOrNoOptions.find(
                                    (option) => option.value === pageData.is_autopay
                                  )
                                : yesOrNoOptions.find((option) => option.value === false)
                            }
                            options={yesOrNoOptions}
                            InputContext={InputContext}
                          />
                          {pageData?.receivables.length > 0 &&
                            pageData?.is_autopay &&
                            isAutoPay && (
                              <button
                                type="button"
                                onClick={() => {
                                  openPaymentModal({
                                    receivable: pageData?.receivables
                                      .filter(
                                        (receivable) =>
                                          receivable.status === "Pending"
                                      )
                                      .sort(
                                        (a, b) => a.due_date - b.due_date
                                      )[0],
                                    recurrence_id: pageData?.id,
                                  });
                                }}
                                className="text-xs bg-gray-100 px-6 py-3 mt-3 rounded-md border cursor-pointer flex flex-row items-center justify-center gap-2 hover:bg-gray-200"
                              >
                                <Settings size={16} /> Payment Config.
                              </button>
                            )}
                        </InputLine>}

                        {!isGlobal && <>
                          <InputLine>
                            <Input
                              type="text"
                              name="card_type"
                              title="Card Type"
                              grow
                              readOnly
                              defaultValue={pageData?.card_type}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="card_number"
                              title="Card Number"
                              grow
                              readOnly
                              defaultValue={pageData?.card_number}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="card_expiration_date"
                              title="Card Expiration Date"
                              grow
                              readOnly
                              defaultValue={pageData?.card_expiration_date}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="card_holder_name"
                              title="Card Holder Name"
                              grow
                              readOnly
                              defaultValue={pageData?.card_holder_name}
                              InputContext={InputContext}
                            />
                          </InputLine>
                        </>}
                      </InputLineGroup>

                      <InputLineGroup
                        title="INVOICES"
                        activeMenu={activeMenu === "invoices"}
                      >
                        {globalSession === null ? (
                          <InputLine>
                            <p className="text-xs text-gray-400 py-2">No session found for this student.</p>
                          </InputLine>
                        ) : (
                          <>
                            <InputLine title="Session">
                              <div className="w-full flex flex-col gap-2 text-xs bg-blue-50 border border-blue-200 rounded-md p-3">
                                <div className="flex gap-6">
                                  <span><span className="text-gray-500">Period:</span> <strong>{globalSession?.session_start_date} → {globalSession?.session_end_date}</strong></span>
                                  <span><span className="text-gray-500">Schedule:</span> <strong>{globalSession?.plan_schedule ?? "-"}</strong></span>
                                  <span><span className="text-gray-500">Payment:</span> <strong className="capitalize">{globalSession?.payment_type ?? "-"}</strong></span>
                                </div>
                              </div>
                            </InputLine>
                            <InputLine title="Payment Method">
                              <SelectPopover
                                name="global_paymentmethod_id"
                                title="Payment Method"
                                isSearchable
                                grow
                                options={paymentMethods}
                                onChange={(el) => setGlobalPaymentMethod(el?.value || null)}
                                InputContext={InputContext}
                              />
                            </InputLine>
                            <InputLine title="Invoices">
                              {(globalSession?.receivables ?? []).length === 0 ? (
                                <p className="text-xs text-gray-400 py-2">No invoices found.</p>
                              ) : (
                                <table className="w-full text-xs border-collapse">
                                  <thead>
                                    <tr className="bg-gray-100">
                                      <th className="text-left px-2 py-1">Type</th>
                                      <th className="text-left px-2 py-1">Due Date</th>
                                      <th className="text-right px-2 py-1">Amount</th>
                                      <th className="text-left px-2 py-1">Status</th>
                                      <th className="text-right px-2 py-1">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {globalSession.receivables.map((inv) => (
                                      <tr key={inv.id} className="border-b even:bg-gray-50">
                                        <td className="px-2 py-1">{inv.type_detail}</td>
                                        <td className="px-2 py-1">{inv.due_date || "-"}</td>
                                        <td className="px-2 py-1 text-right">${parseFloat(inv.amount || 0).toFixed(2)}</td>
                                        <td className="px-2 py-1">
                                          <span className={inv.status === "Paid" ? "text-green-600 font-semibold" : "text-red-500"}>
                                            {inv.status}
                                          </span>
                                        </td>
                                        <td className="px-2 py-1">
                                          {inv.status !== "Paid" && (
                                            <div className="flex flex-row gap-1 justify-end">
                                              <button
                                                type="button"
                                                onClick={() => handleGlobalSendInvoice(inv)}
                                                className="rounded px-2 py-1 flex flex-row items-center gap-1 text-xs bg-slate-300 text-slate-600 border border-slate-400 hover:bg-slate-400 hover:text-white"
                                              >
                                                <Mail size={12} />
                                                <strong>Send</strong>
                                              </button>
                                              <button
                                                type="button"
                                                onClick={() => handleGlobalProcessPayment(inv)}
                                                className="rounded px-2 py-1 flex flex-row items-center gap-1 text-xs bg-green-200 text-green-800 border border-green-400 hover:bg-green-400 hover:text-white"
                                              >
                                                <PlayCircle size={12} />
                                                <strong>Process Payment</strong>
                                              </button>
                                            </div>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </InputLine>
                          </>
                        )}
                      </InputLineGroup>

                      <InputLineGroup
                        title="RECEIVABLES"
                        activeMenu={activeMenu === "receivables"}
                      >
                        {pageData.receivables
                          .sort((a, b) => a.invoice_number - b.invoice_number)
                          .map((receivable, index) => {
                            return (
                              <InputLine
                                key={index}
                                title={index === 0 ? "Receivables" : ""}
                              >
                                <Input
                                  type="hidden"
                                  name="grid_receivable_id"
                                  readOnly={true}
                                  grow
                                  defaultValue={receivable.id}
                                  InputContext={InputContext}
                                />
                                <DatePicker
                                  name="grid_entry_date"
                                  disabled
                                  title="Entry Date "
                                  defaultValue={
                                    parseDateValue(receivable.entry_date)
                                  }
                                  placeholderText="MM/DD/YYYY"
                                  InputContext={InputContext}
                                />
                                <DatePicker
                                  name="grid_due_date"
                                  disabled
                                  title="Due Date "
                                  defaultValue={
                                    parseDateValue(receivable.due_date)
                                  }
                                  placeholderText="MM/DD/YYYY"
                                  InputContext={InputContext}
                                />
                                <Input
                                  type="text"
                                  name="grid_receivable_amount"
                                  readOnly={true}
                                  grow
                                  title="Amount"
                                  defaultValue={formatter.format(
                                    receivable.amount
                                  )}
                                  InputContext={InputContext}
                                />
                                <Input
                                  type="text"
                                  name="grid_discount"
                                  readOnly={true}
                                  grow
                                  title="Discount"
                                  defaultValue={formatter.format(
                                    receivable.discount
                                  )}
                                  InputContext={InputContext}
                                />
                                <Input
                                  type="text"
                                  name="grid_fee"
                                  readOnly={true}
                                  grow
                                  title="Fee"
                                  defaultValue={formatter.format(
                                    receivable.fee
                                  )}
                                  InputContext={InputContext}
                                />
                                <Input
                                  type="text"
                                  name="grid_receivable_total"
                                  readOnly={true}
                                  grow
                                  title="Total"
                                  defaultValue={formatter.format(
                                    receivable.total
                                  )}
                                  InputContext={InputContext}
                                />
                                <Input
                                  type="text"
                                  name="grid_invoice"
                                  readOnly={true}
                                  grow
                                  title="Invoice"
                                  defaultValue={`${receivable.invoice_number
                                    .toString()
                                    .padStart(6, "0")}`}
                                  InputContext={InputContext}
                                />
                                {receivable.status === "Pending" ? (
                                  receivable.paymentMethod.platform ===
                                  "Gravity - Online" ? (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        openPaymentModal({
                                          receivable,
                                          recurrence_id: pageData?.id,
                                          setPaid,
                                        })
                                      }
                                      className="text-xs bg-gray-800 font-bold text-white px-3 py-2 mt-3 rounded-md border cursor-pointer flex flex-row items-center justify-center gap-2 hover:bg-gray-900"
                                    >
                                      <DollarSign size={14} /> Pay
                                    </button>
                                  ) : (
                                    <div className="text-xs bg-gray-500 font-bold text-white px-3 py-2 mt-3 rounded-md border cursor-pointer flex flex-row items-center justify-center gap-2">
                                      <BadgeDollarSign size={14} /> Pending
                                    </div>
                                  )
                                ) : (
                                  <div className="text-xs bg-green-800 font-bold text-white px-3 py-2 mt-3 rounded-md border cursor-pointer flex flex-row items-center justify-center gap-2">
                                    <BadgeDollarSign size={14} /> Paid
                                  </div>
                                )}
                              </InputLine>
                            );
                          })}
                      </InputLineGroup>
                      <InputLineGroup
                        title="Comments"
                        activeMenu={activeMenu === "comments"}
                      >
                        <Comments enrollment_id={student?.enrollments?.[0]?.id} />
                      </InputLineGroup>

                      {isCanceled && (
                        <InputLineGroup
                          title="CANCELED"
                          activeMenu={activeMenu === "canceled"}
                        >
                          <InputLine title="Cancel Reason">
                            <Textarea
                              name="cancel_reason_display"
                              title="Cancel Reason"
                              grow
                              rows={6}
                              readOnly
                              InputContext={InputContext}
                              defaultValue={pageData?.cancel_reason ?? ""}
                            />
                          </InputLine>
                        </InputLineGroup>
                      )}
                    </>
                  ) : (
                    <FormLoading />
                  )}
                </InputContext.Provider>
              </Form>
            </div>
          </div>
        </div>
      ) : null}
    </Preview>
  );
}
